import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { HubTileArtStyles } from '../components/HubTileArt';
import Quotes from '../pages/Quotes';
import Proforma from '../pages/Proforma';
import ToolsDropdown from '../components/ToolsDropdown';
import { LimitedShellContext } from './LimitedShellContext';
import LimitedSidebar from './LimitedSidebar';
import { LIMITED_FONT, GLASS_CARD_STYLE } from './limitedStyles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The fleet photo lives at frontend/public/limited-access-bg.jpg. It is laid under a white wash so
// it reads as a slightly faded backdrop with the glass cards sitting in front of it. If the file is
// missing the gradient wash on the cream base colour still looks intentional.
const LIMITED_SHELL_CSS = `
.c30-limited-root { background-color: #f6efe8; }
.c30-limited-bg {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(255,247,240,0.70) 100%),
    url('/limited-access-bg.jpg');
  background-size: cover;
  background-position: center 45%;
  background-repeat: no-repeat;
}
/* Existing pages paint their own plain grey wrapper (bg-gray-100), or in one case a grey gradient
   wash (PI Counts Warehouse's list view uses from-gray-50/to-gray-100 instead). Make the page root
   and its direct main-content wrapper see-through so the faded photo shows behind the page's own
   white/dark cards instead of flat grey. */
.c30-limited-content > .bg-gray-100,
.c30-limited-content > div > .bg-gray-100,
.c30-limited-content > .from-gray-50.to-gray-100 { background-color: transparent !important; background-image: none !important; }
.c30-limited-scroll { scrollbar-width: none; -ms-overflow-style: none; }
.c30-limited-scroll::-webkit-scrollbar { display: none; }

/* Small screens (tablet / phone in portrait, below Tailwind's md breakpoint of 768px): the Limited UI nav is a floating bar
   along the BOTTOM of the screen (8px gap + 80px tall), so everything a page shows has to stay clear of it. From 768px up the
   nav is the left-hand rail instead and none of this applies. */
@media (max-width: 767.98px) {
  /* 1. Every page's own scrolling content ends above the bar (plus the device's bottom safe area), and anything the browser
        scrolls into view (a focused box, a "scroll to next" jump) stops above the bar too. */
  .c30-limited-content { padding-bottom: calc(104px + env(safe-area-inset-bottom, 0px)) !important; }
  html { scroll-padding-bottom: calc(104px + env(safe-area-inset-bottom, 0px)); }

  /* 2. The content area used to be its own stacking layer (z-10), which trapped every pop-up a page draws itself (modals,
        overlays) UNDER the bar (z-40), however high the page set its z-index. Without that layer, a page's overlays sit above
        the bar exactly as they do in the full dashboard. */
  .c30-limited-content { z-index: auto !important; }

  /* 3. A page's own buttons and bars fixed to the bottom of the screen (for example the PI Counts Close / Save / Send bar, or
        Transport's date bar, month pill and truck-scroll arrows) sit just above the bar instead of underneath it. Full-screen
        overlays are left alone: they have an inset or a top offset, so they never match. */
  .c30-limited-content [class~="fixed"][class*="bottom-"]:not([class*="top-"]):not([class*="inset-"]):not([style*="top"]) {
    translate: 0 calc(-96px - env(safe-area-inset-bottom, 0px));
  }
}
`;

const LimitedShell = ({ user, onLogout, children }) => {
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  const pinnedRef = useRef([]);
  // Which page-as-modal is open (Quotes / Pro Forma / Tools) - null when none. These pages are
  // modal-only in the full dashboard too, so the sidebar opens the same overlay here instead of
  // routing to a page that doesn't exist.
  const [openModal, setOpenModal] = useState(null);

  // Quick links (the burger-menu links), their groups, and this user's pinned sidebar links.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [linksRes, groupsRes, pinsRes] = await Promise.allSettled([
        api.get(`${API}/quick-links`),
        api.get(`${API}/quick-link-groups`),
        api.get(`${API}/limited/sidebar-links`),
      ]);
      if (cancelled) return;
      if (linksRes.status === 'fulfilled' && Array.isArray(linksRes.value.data)) {
        setQuickLinks(linksRes.value.data);
      }
      if (groupsRes.status === 'fulfilled' && Array.isArray(groupsRes.value.data)) {
        setLinkGroups(groupsRes.value.data);
      }
      if (pinsRes.status === 'fulfilled' && Array.isArray(pinsRes.value.data?.link_ids)) {
        pinnedRef.current = pinsRes.value.data.link_ids;
        setPinnedIds(pinsRes.value.data.link_ids);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Optimistic save of the pinned list; rolls back if the server rejects it.
  const applyPins = useCallback(async (next) => {
    const previous = pinnedRef.current;
    pinnedRef.current = next;
    setPinnedIds(next);
    try {
      await api.put(`${API}/limited/sidebar-links`, { link_ids: next });
    } catch (error) {
      pinnedRef.current = previous;
      setPinnedIds(previous);
      toast.error('Could not save your sidebar links');
    }
  }, []);

  const pinLink = useCallback((id) => {
    if (!id || pinnedRef.current.includes(id)) return;
    applyPins([...pinnedRef.current, id]);
  }, [applyPins]);

  const unpinLink = useCallback((id) => {
    applyPins(pinnedRef.current.filter((existing) => existing !== id));
  }, [applyPins]);

  // Move a pinned link so it sits before `beforeId` (or at the end when beforeId is null).
  const movePinned = useCallback((id, beforeId) => {
    const current = pinnedRef.current;
    if (!id || id === beforeId || !current.includes(id)) return;
    const without = current.filter((existing) => existing !== id);
    const at = beforeId ? without.indexOf(beforeId) : -1;
    const next = at === -1
      ? [...without, id]
      : [...without.slice(0, at), id, ...without.slice(at)];
    applyPins(next);
  }, [applyPins]);

  const pinnedLinks = useMemo(
    () => pinnedIds.map((id) => quickLinks.find((link) => link.id === id)).filter(Boolean),
    [pinnedIds, quickLinks]
  );

  const value = useMemo(() => ({
    active: true,
    quickLinks,
    linkGroups,
    pinnedIds,
    pinnedLinks,
    pinLink,
    unpinLink,
    movePinned,
    openLimitedModal: setOpenModal,
    closeLimitedModal: () => setOpenModal(null),
  }), [quickLinks, linkGroups, pinnedIds, pinnedLinks, pinLink, unpinLink, movePinned]);

  return (
    <LimitedShellContext.Provider value={value}>
      <div
        className="c30-limited-root relative min-h-screen"
        data-testid="limited-shell"
        style={{ fontFamily: LIMITED_FONT }}
      >
        <HubTileArtStyles />
        <div className="c30-limited-bg" aria-hidden="true" />
        <LimitedSidebar user={user} onLogout={onLogout} />
        <div className="c30-limited-content relative z-10 pb-[104px] md:pb-0 md:pl-[84px]">
          {children}
        </div>

        {/* Quotes / Pro Forma / Tools - modal-only pages, same components the full dashboard uses */}
        {openModal === 'quotes' && (
          <Quotes.Modal isOpen={true} onClose={() => setOpenModal(null)} mode="create" user={user} />
        )}
        {openModal === 'proforma' && (
          <Proforma.Modal isOpen={true} onClose={() => setOpenModal(null)} mode="create" user={user} />
        )}
        {openModal === 'tools' && (
          <div
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/50 p-2 sm:p-4"
            data-testid="limited-tools-modal"
            onClick={() => setOpenModal(null)}
          >
            <div
              className="w-full sm:w-80 max-h-[80vh] overflow-y-auto rounded-2xl p-3"
              style={GLASS_CARD_STYLE}
              onClick={(e) => e.stopPropagation()}
            >
              <ToolsDropdown user={user} inlineMenu={true} onClose={() => setOpenModal(null)} />
            </div>
          </div>
        )}

        <style>{LIMITED_SHELL_CSS}</style>
      </div>
    </LimitedShellContext.Provider>
  );
};

export default LimitedShell;
